import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const TABS = ["Overview", "Gifts", "Monthly Supporters", "Settings"];
const card = { background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" };
const inputCls = "rounded-lg border px-3 py-2 text-sm bg-transparent";
const th = "px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-left";
const $ = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const d = (s) => (s || "").slice(0, 10);
const Status = ({ s }) => <span className="font-mono text-[10px] uppercase" style={{ color: ["paid", "active", "trialing"].includes(s) ? "var(--teal)" : ["past_due", "refunded", "unpaid"].includes(s) ? "var(--rose-dark)" : "var(--text-muted)" }}>{s}</span>;
const Pill = ({ on, children }) => on ? <span className="rounded-full px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest mr-1" style={{ background: "var(--teal-tint)", color: "var(--teal-deep)" }}>{children}</span> : null;

export function GivingPanel({ openContact }) {
  const [tab, setTab] = useState("Overview");
  return (
    <div data-testid="admin-giving-panel">
      <div className="mb-6"><div className="eyebrow mb-1">Admin · Giving</div><h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Gifts &amp; monthly support.</h1></div>
      <div className="inline-flex p-1 rounded-full border flex-wrap mb-6" style={card}>
        {TABS.map((t) => <button key={t} data-testid={`giving-tab-${t.toLowerCase().replace(" ", "-")}`} onClick={() => setTab(t)} className="px-4 py-1.5 rounded-full text-xs font-medium" style={{ background: tab === t ? "var(--burgundy)" : "transparent", color: tab === t ? "var(--bg-cream)" : "var(--text-obsidian)" }}>{t}</button>)}
      </div>
      {tab === "Overview" && <Overview />}
      {tab === "Gifts" && <Gifts />}
      {tab === "Monthly Supporters" && <Monthly />}
      {tab === "Settings" && <Settings />}
    </div>
  );
}

function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/admin/giving/overview").then((r) => setO(r.data)); }, []);
  if (!o) return null;
  const Stat = ({ k, v, sub }) => <div className="rounded-2xl border p-5" style={card} data-testid={`giving-stat-${k}`}><div className="eyebrow">{k.replace(/_/g, " ")}</div><div className="font-serif text-3xl mt-1" style={{ color: "var(--burgundy)" }}>{v}</div>{sub && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{sub}</div>}</div>;
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat k="one_time_gifts" v={$(o.one_time_total_cents)} sub={`${o.one_time_count} gifts · ${$(o.one_time_this_month_cents)} this month`} />
        <Stat k="active_monthly_supporters" v={o.active_monthly_supporters} sub={o.past_due_monthly ? `${o.past_due_monthly} with payment issue` : "all current"} />
        <Stat k="monthly_recurring" v={$(o.monthly_recurring_cents)} sub="per month, active only" />
        <Stat k="pending_cancellations" v={o.pending_cancellations} sub="end at period close" />
      </div>
      <div className="eyebrow mb-2">Recent gifts</div>
      <Table rows={o.recent} />
      <p className="text-[10px] mt-4 italic" style={{ color: "var(--text-muted)" }}>Stripe {o.mode} mode · Card data is held by Stripe only. Refunds are issued from the Stripe dashboard and reflected here via webhook.</p>
    </div>
  );
}

function Table({ rows, monthly, openContact }) {
  if (!rows?.length) return <div className="text-sm py-6 text-center" style={{ color: "var(--text-muted)" }}>No gifts yet.</div>;
  return (
    <div className="overflow-x-auto"><table className="w-full text-sm"><thead style={{ background: "var(--bg-cream-card)", color: "var(--gold-dark)" }}><tr>
      <th className={th}>Giver</th><th className={th}>Email</th><th className={th}>Amount</th>{!monthly && <th className={th}>Type</th>}<th className={th}>{monthly ? "Started" : "Date"}</th>{monthly && <th className={th}>Next billing</th>}<th className={th}>Status</th><th className={th}></th></tr></thead>
      <tbody>{rows.map((g) => <tr key={g.id} className="border-t" style={{ borderColor: "var(--border-cream)" }} data-testid={`gift-row-${g.id}`}>
        <td className="px-3 py-2 font-serif" style={{ color: "var(--burgundy)" }}>{g.first_name} {g.last_name} <Pill on={g.is_member || g.user_id}>member</Pill></td>
        <td className="px-3 py-2">{g.email_normalized}</td>
        <td className="px-3 py-2">{$(g.amount_cents)}{g.giving_type === "monthly" ? "/mo" : ""}</td>
        {!monthly && <td className="px-3 py-2 text-xs">{g.giving_type === "monthly" ? "Monthly" : "One-time"}</td>}
        <td className="px-3 py-2 text-xs">{d(g.started_at || g.paid_at || g.created_at)}</td>
        {monthly && <td className="px-3 py-2 text-xs">{["cancelled", "canceled"].includes(g.status) ? `ended ${d(g.cancelled_at)}` : g.cancel_at_period_end ? `ends ${d(g.current_period_end)}` : d(g.current_period_end) || "—"}</td>}
        <td className="px-3 py-2"><Status s={g.status} />{g.cancel_at_period_end && !["cancelled", "canceled"].includes(g.status) && <span className="text-[10px] ml-1" style={{ color: "var(--text-muted)" }}>· cancelling</span>}</td>
        <td className="px-3 py-2 text-right">{g.contact_id && <button data-testid={`gift-contact-${g.id}-btn`} onClick={() => openContact?.(g.contact_id)} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Contact</button>}</td>
      </tr>)}</tbody></table></div>
  );
}

function useRows(path, params) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`${path}?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)))}`).then((r) => setRows(r.data || [])), [path, JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return rows;
}

function ContactDrawer({ id, onClose }) {
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/admin/comms/contacts/${id}`).then((r) => setP(r.data)); }, [id]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(48,43,41,0.55)" }} onClick={onClose}>
      <div className="w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg-cream)" }} data-testid="giving-contact-drawer">
        {p && <>
          <div className="eyebrow mb-1">Contact</div>
          <h2 className="font-serif text-2xl mb-1" style={{ color: "var(--burgundy)" }}>{p.contact.first_name} {p.contact.last_name}</h2>
          <div className="text-sm mb-4">{p.contact.email_normalized}</div>
          <div className="mb-4"><Pill on={p.contact.is_member}>member</Pill><Pill on={p.contact.is_giver}>giver</Pill><Pill on={p.contact.subscriber_status === "subscribed"}>subscriber</Pill><Pill on={p.contact.is_event_participant}>events</Pill><Pill on={p.contact.is_customer}>customer</Pill></div>
          <div className="eyebrow mb-2">Giving</div>
          <ul className="text-sm space-y-1">{p.giving.map((g) => <li key={g.id}>{g.giving_type === "monthly" ? "Monthly" : "One-time"} · {$(g.amount_cents)} · {d(g.paid_at || g.started_at || g.created_at)} · <Status s={g.status} /></li>)}</ul>
          <p className="text-[10px] mt-6 italic" style={{ color: "var(--text-muted)" }}>Full profile: Admin → Communications → Contacts.</p>
        </>}
      </div>
    </div>
  );
}

function Gifts() {
  const [q, setQ] = useState(""); const [type, setType] = useState(""); const [status, setStatus] = useState(""); const [cid, setCid] = useState(null);
  const rows = useRows("/admin/giving/gifts", { q, giving_type: type, status });
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="gifts-search-input" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls + " w-64"} style={{ borderColor: "var(--border-cream)" }} />
        <select data-testid="gifts-type-filter" value={type} onChange={(e) => setType(e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}><option value="">Any type</option><option value="one_time">One-time</option><option value="monthly">Monthly</option></select>
        <select data-testid="gifts-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}><option value="">Any status</option><option value="paid">Paid</option><option value="active">Active</option><option value="past_due">Payment issue</option><option value="cancelled">Cancelled</option><option value="refunded">Refunded</option></select>
      </div>
      <Table rows={rows} openContact={setCid} />
      {cid && <ContactDrawer id={cid} onClose={() => setCid(null)} />}
    </div>
  );
}

function Monthly() {
  const [q, setQ] = useState(""); const [status, setStatus] = useState(""); const [cid, setCid] = useState(null);
  const rows = useRows("/admin/giving/monthly", { q, status });
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="monthly-search-input" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls + " w-64"} style={{ borderColor: "var(--border-cream)" }} />
        <select data-testid="monthly-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}><option value="">Any status</option><option value="active">Active</option><option value="past_due">Payment issue</option><option value="cancelled">Cancelled</option></select>
      </div>
      <Table rows={rows} monthly openContact={setCid} />
      {cid && <ContactDrawer id={cid} onClose={() => setCid(null)} />}
    </div>
  );
}

function Settings() {
  const [amts, setAmts] = useState("");
  const [min, setMin] = useState(1);
  useEffect(() => { api.get("/admin/giving/settings").then((r) => { setAmts(r.data.suggested_amounts.join(", ")); setMin(r.data.min_amount); }); }, []);
  const save = async () => {
    const list = amts.split(/[,\s]+/).map((x) => parseInt(x, 10)).filter((n) => n > 0);
    try { const { data } = await api.put("/admin/giving/settings", { suggested_amounts: list }); setAmts(data.suggested_amounts.join(", ")); toast.success("Suggested amounts saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  return (
    <div className="rounded-2xl border p-6 max-w-xl" style={card} data-testid="giving-settings">
      <div className="eyebrow mb-2">Suggested amounts (USD)</div>
      <input data-testid="giving-amounts-input" value={amts} onChange={(e) => setAmts(e.target.value)} className={inputCls + " w-full"} style={{ borderColor: "var(--border-cream)" }} placeholder="10, 25, 50, 100" />
      <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>Shown as quick-select buttons on /give. Minimum gift ${min.toFixed(2)} (Stripe minimum). "Other amount" is always offered.</p>
      <button data-testid="giving-settings-save-btn" onClick={save} className="mt-4 rounded-full px-5 py-2 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save</button>
    </div>
  );
}
